import type { ApiIssue } from './types';

/**
 * The only module that talks HTTP. Same-origin requests with the session cookie; every
 * state-changing request carries the CSRF token (double submit, ADR-0008); errors become a typed
 * ApiError carrying the server's code, field issues and request id.
 */

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT';

/** Error codes the API returns, plus two client-side ones. */
export type ApiErrorCode =
  | 'validation_error'
  | 'bad_request'
  | 'unauthenticated'
  | 'permission_denied'
  | 'not_found'
  | 'document_state_conflict'
  | 'duplicate_document'
  | 'version_conflict'
  | 'idempotency_conflict'
  | 'rate_limited'
  | 'not_implemented'
  | 'internal_error'
  | 'network_error'
  | 'unexpected_response';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode | (string & Record<never, never>);
  readonly issues: ApiIssue[];
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | undefined;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    issues?: ApiIssue[];
    details?: Record<string, unknown>;
    requestId?: string | undefined;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.issues = init.issues ?? [];
    this.details = init.details;
    this.requestId = init.requestId;
  }

  /** Seconds to wait before retrying (rate limiting), when the server said so. */
  get retryAfterSeconds(): number | undefined {
    const value = this.details?.retryAfterSeconds;
    return typeof value === 'number' ? value : undefined;
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

/** The CSRF token the server set as a readable cookie: "__Host-csrf" on HTTPS, "csrf" on HTTP. */
export function readCsrfToken(cookieString: string = document.cookie): string | undefined {
  const jar = new Map<string, string>();
  for (const pair of cookieString.split(';')) {
    const index = pair.indexOf('=');
    if (index < 0) continue;
    jar.set(pair.slice(0, index).trim(), decodeURIComponent(pair.slice(index + 1).trim()));
  }
  const secure = typeof location !== 'undefined' && location.protocol === 'https:';
  const order: readonly string[] = secure ? ['__Host-csrf', 'csrf'] : ['csrf', '__Host-csrf'];
  for (const name of order) {
    const value = jar.get(name);
    if (value) return value;
  }
  return undefined;
}

let unauthenticatedHandler: (() => void) | undefined;

/** Called once by the app: what to do when a request finds the session gone (401). */
export function setUnauthenticatedHandler(handler: () => void): void {
  unauthenticatedHandler = handler;
}

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /** For session probes and sign-in forms: a 401 is an answer there, not an expired session. */
  ignoreUnauthenticated?: boolean;
}

export function buildQuery(query: Record<string, QueryValue> | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const token = readCsrfToken();
    if (token) headers['X-CSRF-Token'] = token;
  }

  let response: Response;
  try {
    response = await fetch(`/api${path}${buildQuery(options.query)}`, {
      method,
      headers,
      credentials: 'same-origin',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({ status: 0, code: 'network_error', message: 'Network request failed' });
  }

  const requestId = response.headers.get('x-request-id') ?? undefined;
  if (response.status === 204) return undefined as T;

  const isJson = (response.headers.get('content-type') ?? '').includes('application/json');
  let payload: unknown;
  try {
    payload = isJson ? await response.json() : await response.text();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({
      status: response.status,
      code: 'unexpected_response',
      message: 'Unreadable response',
      requestId,
    });
  }

  if (response.ok) {
    if (!isJson)
      throw new ApiError({
        status: response.status,
        code: 'unexpected_response',
        message: 'Expected JSON',
        requestId,
      });
    return payload as T;
  }

  const error = toApiError(response.status, payload, requestId);
  if (error.status === 401 && !options.ignoreUnauthenticated) unauthenticatedHandler?.();
  throw error;
}

function toApiError(status: number, payload: unknown, requestId: string | undefined): ApiError {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const body = (payload as { error: unknown }).error;
    if (body && typeof body === 'object' && typeof (body as { code?: unknown }).code === 'string') {
      const e = body as {
        code: string;
        message?: unknown;
        issues?: unknown;
        details?: unknown;
        requestId?: unknown;
      };
      return new ApiError({
        status,
        code: e.code,
        message: typeof e.message === 'string' ? e.message : e.code,
        issues: Array.isArray(e.issues) ? (e.issues as ApiIssue[]) : [],
        ...(e.details && typeof e.details === 'object'
          ? { details: e.details as Record<string, unknown> }
          : {}),
        requestId: typeof e.requestId === 'string' ? e.requestId : requestId,
      });
    }
  }
  return new ApiError({
    status,
    code: status >= 500 ? 'internal_error' : 'unexpected_response',
    message: `HTTP ${status}`,
    requestId,
  });
}

/**
 * Downloads a binary response (e.g. a PDF) from a same-origin API path and hands it to the browser
 * as a file. Errors become ApiError like any other request.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<string> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, { credentials: 'same-origin' });
  } catch {
    throw new ApiError({ status: 0, code: 'network_error', message: 'Network request failed' });
  }
  const requestId = response.headers.get('x-request-id') ?? undefined;
  if (!response.ok) {
    const isJson = (response.headers.get('content-type') ?? '').includes('application/json');
    const payload: unknown = isJson ? await response.json().catch(() => null) : null;
    const error = toApiError(response.status, payload, requestId);
    if (error.status === 401) unauthenticatedHandler?.();
    throw error;
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return name;
}
